import { Value } from 'firebase/remote-config'
import { initialValues as defaultFeatureFlags } from 'services/FeatureFlags/reducer'

import {
  BRAND_LIST,
  Countries,
  FeatureFlagKeys,
  FeatureFlags,
  FeatureFlagValue,
} from 'typings'

export enum Media {
  APP = 'app',
  WEB = 'web',
}

interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

export const getBackupFeatureFlags: (
  brand: BRAND_LIST,
  country: Countries,
) => FeatureFlags = (brand, country) =>
  // @ts-ignore
  ({})

const getTypedValue = (value: Value, typeString: string): FeatureFlagValue => {
  switch (typeString) {
    case 'string':
      return value.asString()
    case 'boolean':
      return value.asBoolean()
    case 'number':
      return value.asNumber()
    default:
      return value.asString()
  }
}

const MINIMUM_FETCH_INTERVAL = 15 * 60 * 1000

/**
 * Initializes firebase remote config and fetches feature flags
 */
export const initFirebaseRemoteConfig = async (
  firebaseConfig: FirebaseConfig,
  brand: BRAND_LIST,
  country: Countries,
  onLoad: (featureFlags: FeatureFlags) => void,
) => {
  const [
    { initializeApp },
    { fetchAndActivate, getRemoteConfig, getValue },
  ] = await Promise.all([
    import('@firebase/app'),
    import('@firebase/remote-config'),
  ])

  const app = initializeApp(firebaseConfig)
  let successfulFetch = false

  const remoteConfig = getRemoteConfig(app)
  remoteConfig.settings.minimumFetchIntervalMillis = MINIMUM_FETCH_INTERVAL
  remoteConfig.defaultConfig = Object.values(FeatureFlagKeys).reduce(
    (acc, current) => ({
      ...acc,
      key: defaultFeatureFlags[current],
    }),
    {},
  )

  const fetchAndStoreRemoteConfig = () =>
    fetchAndActivate(remoteConfig)
      .then(() => {
        const featureFlags = Object.values(FeatureFlagKeys).reduce(
          (acc, key) => {
            const featureFlagValue = getTypedValue(
              getValue(remoteConfig, key),
              typeof defaultFeatureFlags[key],
            )
            return {
              ...acc,
              [key]: featureFlagValue,
            }
          },
          {} as Partial<FeatureFlags>,
        ) as FeatureFlags

        successfulFetch = true
        onLoad(featureFlags)
      })
      .catch(e => {
        console.error('Error while retrieving feature flags: ', e)

        if (!successfulFetch) {
          onLoad(getBackupFeatureFlags(brand, country))
        }
      })
  fetchAndStoreRemoteConfig()
  setInterval(fetchAndStoreRemoteConfig, MINIMUM_FETCH_INTERVAL)
}
